import React, { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import { swallow } from "@/lib/log";
import { playSound, startAlarm } from "@/lib/sounds";
/**
 * Polls /api/reminders/due every N seconds, shows toast when a NEW due reminder is found.
 * Also calls /api/activity/heartbeat to update last_active for Digital Legacy.
 * Renders nothing.
 */
export default function NotificationPoller() {
  const { token } = useAuth();
  const seenRef = useRef(new Set(JSON.parse(localStorage.getItem("impo_notified") || "[]")));
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try { await api.post("/activity/heartbeat"); } catch (e) { swallow("NotificationPoller")(e); }
      try {
        const { data } = await api.get("/reminders/due");
        for (const r of data || []) {
          if (seenRef.current.has(r.id)) continue;
          seenRef.current.add(r.id);
          try {
            const time = r.due_at ? new Date(r.due_at).toLocaleString() : "now";
            let alarmHandle = null;
            if (r.sound && r.sound !== "none") {
              try {
                // Continuous alarm — hard capped at 60s inside startAlarm().
                alarmHandle = startAlarm(r.sound, { maxDurationMs: 60_000 });
              } catch (e) { swallow("NotificationPoller/alarm")(e); }
            }
            toast(`⏰ ${r.text}`, {
              description: `Reminder — ${time}`,
              duration: 60_000,
              action: {
                label: "Dismiss",
                onClick: () => { try { alarmHandle?.stop(); } catch { /* noop */ } },
              },
              onDismiss: () => { try { alarmHandle?.stop(); } catch { /* noop */ } },
              onAutoClose: () => { try { alarmHandle?.stop(); } catch { /* noop */ } },
            });
          } catch (e) { swallow("NotificationPoller")(e); }
        }
        localStorage.setItem("impo_notified", JSON.stringify(Array.from(seenRef.current).slice(-200)));
        // publish count via custom event for BottomNav badge
        window.dispatchEvent(new CustomEvent("impo:due-count", { detail: { count: (data || []).length } }));
      } catch (e) { swallow("NotificationPoller")(e); }
    };

    tick();
    intervalRef.current = setInterval(tick, 30_000);
    return () => { cancelled = true; if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [token]);

  return null;
}
